import c from 'classnames';
import separatedElements from 'core/components/common/separatedElements';
import { showSnackbar } from 'core/components/snackbar';
import { DEFAULT_SOON_CAUSE, ProductComplexity } from 'core/const';
import { icon } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonth, formatToLocalTime, isFilledArray, isImpossibleDate, isMobile } from 'core/utils';
import {
    getFormFirstDate,
    getFormItemsCount,
    getFormSoonCause,
    IGetFormItemsCountProductForm,
    isProductFormSubscriptionAccess,
    IGetFormFirstDateProductForm,
} from 'core/utils/product-forms/productForms';
import React from 'react';
import { Account, Lecturer, Product, ProductForm } from 'site/graphql';
import CalendarIcon from '../../../svg/calendar.svg';
import certificateIcon from '../../../svg/certificate.svg';
import ClockIcon from '../../../svg/clock.svg';
import eventRecordIcon from '../../../svg/event-record.svg';
import LecturerIcon from '../../../svg/lecturer.svg';
import materialIcon from '../../../svg/material.svg';
import offerIcon from '../../../svg/pack.svg';
import RubleIcon from '../../../svg/ruble.svg';
import seminarIcon from '../../../svg/seminar.svg';
import ComplexityIcon from '../../../svg/trending-up.svg';
import videoCourseIcon from '../../../svg/video-course.svg';
import webinarIcon from '../../../svg/webinar.svg';

type IFeatureType = 'type' | 'complexity' | 'lecturers' | 'quantity' | 'start' | 'price';

export type IProductFeaturesProductForm = Pick<
    ProductForm,
    'id' | 'type' | 'price' | 'subscriptionAccess' | 'showNumberLessons'
> &
    IGetFormItemsCountProductForm &
    IGetFormFirstDateProductForm;
export type IProductFeaturesProduct = Pick<Product, 'id' | 'complexity'> & {
    lecturers: (Pick<Lecturer, 'id'> & { account: Pick<Account, 'id' | 'firstName' | 'lastName'> })[];
};

type IProductFeaturesProps = {
    mode: 'main' | 'side';
    features: IFeatureType[];
    productForm: IProductFeaturesProductForm;
    product: IProductFeaturesProduct;
} & WithStyles<typeof styles>;

class ProductFeatures extends React.Component<IProductFeaturesProps> {
    renderType(showSubscriptionStar: boolean) {
        const { productForm, classes } = this.props;
        let Icon: React.StatelessComponent<IIconProps>;
        let text: string;

        switch (productForm.type) {
            case 'WEBINAR':
                Icon = webinarIcon;
                text = 'Вебинар';
                break;
            case 'SEMINAR': {
                Icon = seminarIcon;
                text = 'Семинар';
                break;
            }
            case 'EVENT_RECORD':
                Icon = eventRecordIcon;
                text = 'Видеозапись';
                break;
            case 'VIDEO_COURSE':
                Icon = videoCourseIcon;
                text = 'Видеокурс';
                break;
            case 'MATERIAL':
                Icon = materialIcon;
                text = 'Материал';
                break;
            case 'CERTIFICATE':
                Icon = certificateIcon;
                text = 'Сертификат';
                break;
            case 'OFFER':
                Icon = offerIcon;
                text = 'Программа обучения';
                break;
            default:
                return null;
        }

        return (
            <div className={classes.feature}>
                <Icon className={classes.icon} />
                <p
                    onClick={() =>
                        showSubscriptionStar &&
                        isMobile() &&
                        showSnackbar({ snackbarMessage: 'Доступ при покупке любого курса' })
                    }
                    className={c(classes.text, { [classes.subscriptionStar]: showSubscriptionStar })}
                    title={showSubscriptionStar ? 'Доступ при покупке любого курса' : null}
                >
                    {text}
                </p>
            </div>
        );
    }

    renderComplexity() {
        const {
            classes,
            product: { complexity },
        } = this.props;

        return (
            <div className={classes.feature}>
                <ComplexityIcon className={classes.icon} />
                <p className={classes.text}>Уровень: {ProductComplexity[complexity]}</p>
            </div>
        );
    }

    renderLecturers() {
        const {
            classes,
            product: { lecturers },
        } = this.props;

        return (
            isFilledArray(lecturers) && (
                <div className={classes.feature}>
                    <LecturerIcon className={classes.icon} />
                    <div className={classes.text}>
                        {separatedElements(lecturers, lecturer => (
                            <span key={lecturer.id}>
                                {lecturer.account.firstName} {lecturer.account.lastName}
                            </span>
                        ))}
                    </div>
                </div>
            )
        );
    }

    renderQuantity() {
        const { classes, productForm } = this.props;

        return (
            productForm.type !== 'OFFER' && (
                <div className={classes.feature}>
                    <ClockIcon className={classes.icon} />
                    <p className={classes.text}>Количество занятий: {getFormItemsCount(productForm)}</p>
                </div>
            )
        );
    }

    renderStart() {
        const { classes, productForm } = this.props;
        const startDate = ['WEBINAR', 'SEMINAR'].includes(productForm.type) && getFormFirstDate(productForm);

        return (
            startDate && (
                <div className={classes.feature}>
                    <CalendarIcon className={classes.icon} />
                    <p className={classes.text}>
                        {isImpossibleDate(startDate)
                            ? getFormSoonCause(productForm) || DEFAULT_SOON_CAUSE
                            : `Начало: ${formatToLocalDayMonth(startDate)} ${formatToLocalTime(startDate)}`}
                    </p>
                </div>
            )
        );
    }

    renderPrice(showSubscriptionStar: boolean) {
        const { classes, productForm } = this.props;

        return (
            <div className={classes.feature}>
                <RubleIcon className={classes.icon} />
                <p
                    className={c(classes.text, { [classes.subscriptionStar]: showSubscriptionStar })}
                    title={showSubscriptionStar ? 'Доступ при покупке любого курса' : null}
                    onClick={() =>
                        showSubscriptionStar &&
                        isMobile() &&
                        showSnackbar({ snackbarMessage: 'Доступ при покупке любого курса' })
                    }
                >
                    Стоимость: {productForm.price === 0 ? 'Бесплатно' : `${productForm.price} РУБ`}
                </p>
            </div>
        );
    }

    render() {
        const { mode, classes, features, productForm } = this.props;
        const showSubscriptionStar = isProductFormSubscriptionAccess(productForm);

        return (
            <div
                className={c(classes.wrap, {
                    [classes.sideMode]: mode === 'side',
                })}
            >
                {features.includes('type') && this.renderType(showSubscriptionStar)}
                {features.includes('complexity') && this.renderComplexity()}
                {features.includes('lecturers') && this.renderLecturers()}
                {features.includes('quantity') && productForm.showNumberLessons && this.renderQuantity()}
                {features.includes('start') && this.renderStart()}
                {features.includes('price') && this.renderPrice(showSubscriptionStar)}
            </div>
        );
    }
}

const styles = createStyles({
    wrap: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        display: 'flex',
    },
    sideMode: {
        '& $feature': {
            justifyContent: 'center',
            '& $text': {
                width: 'auto',
            },
        },
    },
    feature: {
        flex: '1 0 auto',
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        marginBottom: 10,
        paddingRight: 5,
        boxSizing: 'border-box',
        minWidth: 50 + '%',
        maxWidth: 100 + '%',
    },
    icon: {
        flex: 'none',
        ...icon,
        marginRight: 0.75 + 'rem',
        fill: 'white',
    },
    text: {
        color: '#999999',
        margin: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        width: 300,
    },
    subscriptionStar: {
        '&:after': {
            content: '" *"',
        },
    },
});
export default injectSheet(styles, 'ProductFeatures')(ProductFeatures);
